import { useState } from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import axios from "axios";
import { useNavigate, Link } from 'react-router-dom';
import '../style.css';

function SignUp() {
    const navigate = useNavigate();
    const [user, setUser] = useState({ name: '', email: '', phone: '', password: '', resume: null });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setUser(prev => ({
            ...prev,
            resume: file
        }));
        if (errors.resume) {
            setErrors(prev => ({ ...prev, resume: '' }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUser(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        let newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[0-9]{10}$/; // Simple 10-digit validation

        if (!user.name.trim()) newErrors.name = "Name is required";
        if (!user.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!emailRegex.test(user.email)) {
            newErrors.email = "Invalid email format";
        }
        if (!user.phone.trim()) {
            newErrors.phone = "Phone number is required";
        } else if (!phoneRegex.test(user.phone)) {
            newErrors.phone = "Phone number must be 10 digits";
        }
        if (!user.password.trim()) {
            newErrors.password = "Password is required";
        } else if (user.password.length < 6) {
            newErrors.password = "Password must be at least 6 characters";
        }
        if (!user.resume) {
            newErrors.resume = "Resume is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setIsLoading(true);

        const formData = new FormData();
        formData.append('name', user.name);
        formData.append('email', user.email);
        formData.append('phone', user.phone);
        formData.append('password', user.password);
        formData.append('resume', user.resume);

        try {
            const response = await axios.post(`${SERVER_URL}/signup`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            alert(response.data.message || "User created successfully");
            navigate('/login');
        } catch (error) {
            console.error("Signup Error:", error);
            // Handle specific backend errors (e.g. "Email already exists")
            const errorMessage = error.response?.data?.detail || "Signup failed. Please try again.";
            if (errorMessage.toLowerCase().includes("email")) {
                setErrors(prev => ({ ...prev, email: errorMessage }));
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="app-container">
            <div className="card">
                <div className="text-center mb-4">
                    <h2 className="text-primary mb-2">Sign Up</h2>
                    <p className="text-secondary mb-0">Join us and start your journey</p>
                </div>

                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3 text-start" controlId="name">
                        <Form.Label className="form-label">Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter Name"
                            value={user.name}
                            onChange={handleChange}
                            name="name"
                            isInvalid={!!errors.name}
                            className="form-control"
                        />
                        <Form.Control.Feedback type="invalid" className="text-error">
                            {errors.name}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3 text-start" controlId="email">
                        <Form.Label className="form-label">Email</Form.Label>
                        <Form.Control
                            type="email"
                            placeholder="Enter Email"
                            value={user.email}
                            onChange={handleChange}
                            name="email"
                            isInvalid={!!errors.email}
                            className="form-control"
                        />
                        <Form.Control.Feedback type="invalid" className="text-error">
                            {errors.email}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3 text-start" controlId="phone">
                        <Form.Label className="form-label">Phone</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter Phone Number"
                            value={user.phone}
                            onChange={handleChange}
                            name="phone"
                            isInvalid={!!errors.phone}
                            className="form-control"
                        />
                        <Form.Control.Feedback type="invalid" className="text-error">
                            {errors.phone}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3 text-start" controlId="password">
                        <Form.Label className="form-label">Password</Form.Label>
                        <Form.Control
                            type="password"
                            placeholder="Enter Password"
                            value={user.password}
                            onChange={handleChange}
                            name="password"
                            isInvalid={!!errors.password}
                            className="form-control"
                        />
                        <Form.Control.Feedback type="invalid" className="text-error">
                            {errors.password}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3 text-start" controlId="resume">
                        <Form.Label className="form-label">Upload Resume</Form.Label>
                        <Form.Control
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileChange}
                            name="resume"
                            isInvalid={!!errors.resume}
                            className="form-control"
                        />
                        <Form.Control.Feedback type="invalid" className="text-error">
                            {errors.resume}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Button variant="primary" type="submit" className="btn btn-primary mt-3" disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                <span className="ms-2">Signing Up...</span>
                            </>
                        ) : (
                            "Sign Up"
                        )}
                    </Button>
                </Form>

                <div className="text-center mt-3">
                    <p className="text-secondary">
                        Already have an account? <Link to="/login" className="text-custom fw-bold text-decoration-none">Login</Link>
                    </p>
                </div>
            </div>
        </div>
    );

}

export default SignUp;